import app from 'flarum/forum/app';
import { override } from 'flarum/common/extend';
import Button from 'flarum/common/components/Button';
import Post from 'flarum/common/models/Post';
import PostLoading from 'flarum/forum/components/LoadingPost';
import PostStream from 'flarum/forum/components/PostStream';
import ReplyPlaceholder from 'flarum/forum/components/ReplyPlaceholder';
import dayjs from 'dayjs';

export default () => {
    override(PostStream.prototype, 'view', function() {
        // The majority of this is direct from flarum, overridden to add custom 
        // DiscussionPost and CommentPost components (in comment-post.jsx), style them,
        // and move the time gaps outside of the posts to match design.
        // Our changes here start at replacing the timeGaps
        let lastTime;

        const viewingEnd = this.stream.viewingEnd();
        const posts = this.stream.posts();
        const postIds = this.discussion.postIds();

        const postFadeIn = (vnode) => {
          $(vnode.dom).addClass('fadeIn');
          // 500 is the duration of the fadeIn CSS animation + 100ms,
          // so the animation has time to complete
          setTimeout(() => $(vnode.dom).removeClass('fadeIn'), 500);
        };

        const items = posts.map((post, i) => {
          let content;
          const attrs = { 'data-index': this.stream.visibleStart + i };

          if (post) {
            const time = post.createdAt();
            const PostComponent = app.postComponents[post.contentType()];
            content = !!PostComponent && <PostComponent post={post} />;

            attrs.key = 'post' + post.id();
            attrs.oncreate = postFadeIn;
            attrs['data-time'] = time.toISOString();
            attrs['data-number'] = post.number();
            attrs['data-id'] = post.id();
            attrs['data-type'] = post.contentType();
          } else {
            attrs.key = 'post' + postIds[this.stream.visibleStart + i];
            content = <PostLoading />;
          }

          return (
            <div className="PostStream-item" {...attrs}>
              {content}
            </div>
          );
        });

        if (!viewingEnd && posts[this.stream.visibleEnd - this.stream.visibleStart - 1]) {
          items.push(
            <div className="PostStream-loadMore" key="loadMore">
              <Button className="Button" onclick={this.stream.loadNext.bind(this.stream)}>
                {app.translator.trans('core.forum.post_stream.load_more_button')}
              </Button>
            </div>
          );
        }

        // Allow extensions to add items to the end of the post stream.
        if (viewingEnd) {
          items.push(...this.endItems().toArray());
        }

        // If we're viewing the end of the discussion, the user can reply, and
        // is not already doing so, then show a 'write a reply' placeholder.
        if (viewingEnd && (app.session.user || this.discussion.canReply())) {
          items.push(
            <div className="PostStream-item" key="reply" data-index={this.stream.count()} oncreate={postFadeIn}>
              <ReplyPlaceholder discussion={this.discussion} />
            </div>
          );
        }

        // If the post before this one was more than 4 days ago, we will
        // display a 'time gap' indicating how long it has been in between
        // the posts. Skip the last item, which is the reply placeholder
        items.map((item, index) => {
            if (index === items.length - 1) return;

            const time = dayjs(item.attrs['data-time']);
            if (lastTime && time - lastTime > 1000 * 60 * 60 * 24 * 4) {
                const timeGapContent = (
                    <div className="PostStream-timeGap" key={`timeGap${index}`}>
                        <span>{app.translator.trans('core.forum.post_stream.time_lapsed_text', { period: dayjs().add(time - lastTime, 'ms').fromNow(true) })}</span>
                    </div>
                );

                // splice the time gap content into the items array
                items.splice(index, 0, timeGapContent);
            }
            lastTime = time;
        });

        // Add the mentionedBy class to posts that mention another post
        items.map(item => stylePostsWithMentions(item.children[0]?.attrs?.post));

        // If the user is not logged in, redact user information from the post
        if (!app.session.user) {
          items.map(item => redactUserNames(item.children[0]?.attrs?.post));

          // Hide Event posts in the stream, such as "discussion tag changed by 'x' user"
          this.$('.PostStream-item > .EventPost').hide();
        }

        return (
            <div className="PostStream" role="feed" aria-live="off" aria-busy={this.stream.pagesLoading ? 'true' : 'false'}>
              {items}
            </div>
          );
    });
};

// If this post mentions another
function stylePostsWithMentions(post) {
  if (!post || !(post instanceof Post)) return;

  let mentionedByHtml = post.contentHtml();
  const hasMentionedTag = mentionedByHtml?.includes('PostMention');

  if (!hasMentionedTag) return;

  mentionedByHtml = mentionedByHtml.replace('<p>', '<p class="mentionedBy">');
  post.data.attributes.contentHtml = mentionedByHtml;
}

function redactUserNames(post) {
  if (!post || !(post instanceof Post)) return;

  const postText = post.contentHtml();

  // remove names from replies
  let redactedText = postText.replace(/<a[^>]*class="PostMention"[^>]*>(.*?)<\/a>/g, function() {
    return `<a href="#" class="PostMention">[redacted]</a>`;
  });

  // remove names from mentions
  redactedText = redactedText.replace(/<a[^>]*class="UserMention"[^>]*>(.*?)<\/a>/g, function() {
    return `<a href="#" class="UserMention">@[redacted]</a>`;
  });
  
  post.data.attributes.contentHtml = redactedText;
}
